import { MINIMUM_ZOOM_LEVEL } from 'App/Map/MapContext/MapBase'
import { cloneDeep, groupBy, mapValues, maxBy } from 'lodash'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { parse as parseWKT } from 'wellknown'
import { getActiveNodeStatus } from '../../Map/_utils/getActiveNodeStatus'
import type {
  TActiveGateway,
  TActiveNode,
  TLures,
  TNodeLog,
  TNodeServiceIssueSummary,
  TNodeStatus,
  TPlannedGateway,
  TPlannedNode,
} from '../../types'
import { getIdentifier } from '../../utils/getIdentifier'
import { nodeBelongsToProperty } from '../../utils/nodeBelongsToProperty'
import type {
  NodeRemoveReasons,
  NodeSwapReasons,
  TEquipmentType,
  TNodeDeviceType,
} from '../serviceCenterStore'
import { serviceCenterStore } from '../serviceCenterStore'

export const actions = {
  setMapEquipmentStatuses: (mapEquipmentStatuses: TNodeStatus[]) => {
    serviceCenterStore.setState((s) => ({ ...s, mapEquipmentStatuses }))
  },
  setMapEquipmentTypes: (mapEquipmentTypes: TEquipmentType[]) => {
    serviceCenterStore.setState((s) => {
      const { mapEquipmentDevices } = s

      return {
        ...s,
        mapEquipmentTypes,
        // Reset devices selection if 'station' type is removed from selected types
        mapEquipmentDevices: !mapEquipmentTypes.includes('station') ? [] : mapEquipmentDevices,
      }
    })
  },
  setMapEquipmentDevices: (mapEquipmentDevices: TNodeDeviceType[]) => {
    serviceCenterStore.setState((s) => ({ ...s, mapEquipmentDevices }))
  },
  setNodeSwapReasons: (swapReasons: NodeSwapReasons) => {
    serviceCenterStore.setState((s) => ({ ...s, swapReasons }))
  },
  setNodeRemoveReasons: (removeReasons: NodeRemoveReasons) => {
    serviceCenterStore.setState((s) => ({ ...s, removeReasons }))
  },
  // Warning: only use this function when downloading planned nodes from all user properties
  setPlannedNodes: (plannedNodes: Array<TPlannedNode | TPlannedGateway>) => {
    serviceCenterStore.setState((s) => ({ ...s, plannedNodes }))
  },
  refreshPlannedNodesForProperties: (
    plannedNodes: Array<TPlannedNode | TPlannedGateway>,
    propertyIds: number[],
  ) => {
    serviceCenterStore.setState((s) => ({
      ...s,
      plannedNodes: [
        // Remove planned nodes from the properties that are being refreshed
        ...s.plannedNodes.filter((n) => !propertyIds.includes((n as TPlannedNode).propertyId)),
        // Add new planned nodes
        ...plannedNodes,
      ],
    }))
  },
  updatePlannedNodes: (plannedNodes: Array<TPlannedNode | TPlannedGateway>) => {
    const plannedNodeIds = plannedNodes.map((pnode) => pnode.id)

    serviceCenterStore.setState((s) => ({
      ...s,
      plannedNodes: [...s.plannedNodes.filter((n) => !plannedNodeIds.includes(n.id)), ...plannedNodes],
    }))
  },
  repositionPseudoNodes: (params: { [plannedNodeId: string]: string }) => {
    const relocatingPnodeIds = Object.keys(params)
    const plannedNodes = serviceCenterStore.selectors.getAllPlannedNodes(serviceCenterStore.getState())

    const relocatedPnodes = relocatingPnodeIds.map((pnodeId) => {
      const relocatedPnode = plannedNodes.find(
        (pnode) => pnode.id === pnodeId || (pnode as TPlannedNode)?.pseudogatewayId === parseInt(pnodeId),
      )

      const updatedPnode = cloneDeep({
        ...relocatedPnode,
        location: JSON.stringify(parseWKT(params[pnodeId] as string)),
      })

      return updatedPnode as TPlannedNode
    })

    serviceCenterStore.actions.updatePlannedNodes(relocatedPnodes)
  },
  repositionActiveNode: (params: {
    gatewayIdentifier?: string
    nodeIdentifier?: string
    location: string
  }) => {
    let updatingNode

    if (params.gatewayIdentifier) {
      updatingNode = serviceCenterStore
        .getState()
        .activeNodes.filter(
          (gateway) =>
            (gateway as TActiveGateway).gatewayType && gateway.gatewayIdentifier === params.gatewayIdentifier,
        )[0]
    }

    if (params.nodeIdentifier) {
      updatingNode = serviceCenterStore
        .getState()
        .activeNodes.filter((node) => (node as TActiveNode).nodeIdentifier === params.nodeIdentifier)[0]
    }

    if (updatingNode) {
      const shapedActiveNode = {
        ...updatingNode,
        location: JSON.stringify(parseWKT(params.location)),
      }

      serviceCenterStore.actions.updateActiveNode(shapedActiveNode)
    }
  },
  removePlannedNodes(nodeIds: string[]) {
    serviceCenterStore.setState((s) => {
      const { plannedNodes, selectedPlannedEquipmentIds } = s
      const updatedPlannedNodes = plannedNodes.filter((pnode) => !nodeIds.includes(pnode.id))
      const updatedPlannedEquipmentIds = selectedPlannedEquipmentIds.filter((id) => !nodeIds.includes(id))

      return {
        ...s,
        plannedNodes: updatedPlannedNodes,
        selectedPlannedEquipmentIds: updatedPlannedEquipmentIds,
      }
    })
  },
  removeActiveNodes(nodeIds: string[]) {
    serviceCenterStore.setState((s) => {
      const { activeNodes, selectedActiveEquipmentIds } = s
      const updatedActiveNodes = activeNodes.filter((node) => !nodeIds.includes(getIdentifier(node)))
      const updatedActiveEquipmentIds = selectedActiveEquipmentIds.filter((id) => !nodeIds.includes(id))

      return {
        ...s,
        activeNodes: updatedActiveNodes,
        selectedActiveEquipmentIds: updatedActiveEquipmentIds,
      }
    })
  },
  // Warning: only use this function when downloading nodes from all user properties
  setActiveNodes: (activeNodes: Array<TActiveNode | TActiveGateway>) => {
    serviceCenterStore.setState((s) => ({ ...s, activeNodes }))
  },
  refreshActiveNodesForProperties: (
    activeNodes: Array<TActiveNode | TActiveGateway>,
    propertyIds: number[],
  ) => {
    serviceCenterStore.setState((s) => ({
      ...s,
      activeNodes: [
        // Remove active nodes from the properties that are being refreshed
        ...s.activeNodes.filter(
          (n) => !propertyIds.some((propertyId) => nodeBelongsToProperty(n, propertyId)),
        ),
        // Add new active nodes
        ...activeNodes,
      ],
    }))
  },
  addActiveNode: (activeNode: TActiveNode | TActiveGateway) => {
    const nodeId = getIdentifier(activeNode)

    serviceCenterStore.setState((s) => ({
      ...s,
      activeNodes: [...s.activeNodes.filter((n) => getIdentifier(n) !== nodeId), activeNode],
    }))
  },
  updateActiveNode: (activeNode: TActiveNode | TActiveGateway) => {
    const nodeId = getIdentifier(activeNode)

    serviceCenterStore.setState((s) => ({
      ...s,
      activeNodes: [...s.activeNodes.filter((n) => getIdentifier(n) !== nodeId), activeNode],
    }))
  },
  removeActiveNode: (nodeIdentifier: string) => {
    serviceCenterStore.setState((s) => ({
      ...s,
      activeNodes: s.activeNodes.filter((node) => (node as TActiveNode).nodeIdentifier !== nodeIdentifier),
    }))
  },
  // Warning: only use this function when downloading node logs from all user properties
  setNodeLogs: (nodeLogs: Record<string, TNodeLog>) => {
    const mostRecentLogTime = maxBy(Object.values(nodeLogs), 'stamp')?.stamp || null

    const nodeStatuses: Record<string, TNodeStatus> = mapValues(nodeLogs, (nodeLog) =>
      getActiveNodeStatus(nodeLog),
    )

    serviceCenterStore.setState((s) => ({ ...s, nodeLogs, nodeStatuses, mostRecentLogTime }))
  },
  refreshNodeLogsForProperties: (nodeLogs: Record<string, TNodeLog>) => {
    const mostRecentLogTime = maxBy(Object.values(nodeLogs), 'stamp')?.stamp || null

    const nodeStatuses: Record<string, TNodeStatus> = mapValues(nodeLogs, (nodeLog) =>
      getActiveNodeStatus(nodeLog),
    )

    serviceCenterStore.setState((s) => ({
      ...s,
      nodeLogs: {
        ...s.nodeLogs,
        ...nodeLogs,
      },
      nodeStatuses: {
        ...s.nodeStatuses,
        ...nodeStatuses,
      },
      mostRecentLogTime,
    }))
  },
  refreshServiceIssuesForProperties: (
    serviceIssues: Record<string, TNodeServiceIssueSummary[]>,
    propertyIds: number[],
  ) => {
    serviceCenterStore.setState((s) => {
      // Remove service issues from the properties that are being refreshed
      const filteredServiceIssues = Object.values(s.serviceIssues)
        .flat()
        .filter((n) => n.propertyId && !propertyIds.includes(n.propertyId))

      const groupedServiceIssues = groupBy(filteredServiceIssues, 'nodeIdentifier')

      return {
        ...s,
        serviceIssues: {
          // Existing service issues minus those from the properties that are being refreshed
          ...groupedServiceIssues,
          // New service issues
          ...serviceIssues,
        },
      }
    })
  },
  selectProperty: (propertyId: number | null) => {
    selectedFieldAssetsStore.setState((s) => ({ ...s, property: propertyId }))
  },
  selectEquipment: (equipmentId: string | null, type: 'planned' | 'active') => {
    if (type === 'planned') {
      serviceCenterStore.setState((s) => ({
        ...s,
        selectedPlannedEquipmentIds: equipmentId ? [equipmentId] : [],
        selectedActiveEquipmentIds: [],
      }))
    } else {
      serviceCenterStore.setState((s) => ({
        ...s,
        selectedPlannedEquipmentIds: [],
        selectedActiveEquipmentIds: equipmentId ? [equipmentId] : [],
      }))
    }
  },
  unselectEquipment: () => {
    serviceCenterStore.setState((s) => ({
      ...s,
      selectedPlannedEquipmentIds: [],
      selectedActiveEquipmentIds: [],
    }))
  },
  setLures: (lures: TLures) => {
    serviceCenterStore.setState((s) => ({ ...s, lures }))
  },
  setOfflineContentsDownloadStatus: (newStatus: 'default' | 'success' | 'error' | string) => {
    const getProgression = (newStatus: string, currentStatus: string) => {
      const parsedNewStatus = parseInt(newStatus)
      const parsedCurrentStatus = parseInt(currentStatus)

      // set status default, error, or progression number
      if (isNaN(parsedNewStatus)) {
        return newStatus
      } else if (isNaN(parsedCurrentStatus) && !isNaN(parsedNewStatus)) {
        return newStatus
      } else {
        return (parsedNewStatus + parsedCurrentStatus).toString()
      }
    }

    serviceCenterStore.setState((s) => ({
      ...s,
      offlineContents: {
        ...s.offlineContents,
        downloadStatus: getProgression(newStatus, s.offlineContents.downloadStatus),
        lastTimeDownloaded:
          newStatus === 'success' ? new Date().toISOString() : s.offlineContents.lastTimeDownloaded,
      },
    }))
  },
  setIsOfflineContentBannerOpened: (newState: boolean) => {
    serviceCenterStore.setState((s) => ({
      ...s,
      offlineContents: {
        ...s.offlineContents,
        isBannerOpened: newState,
      },
    }))
  },
  getPropertyIdByMapBounds: (mapBounds: google.maps.LatLngBounds) => {
    const properties = fieldAssetStore.getState().properties

    if (!properties) return

    const foundProperty = Object.values(properties).find((property) => {
      const latLng = {
        lat: property.centroid.coordinates.lat,
        lng: property.centroid.coordinates.lng,
      }

      return mapBounds.contains(latLng)
    })

    return foundProperty?.propertyId || null
  },
  setPropertyOverviewZoomLevel: (zoomLevel?: number | null) => {
    // When user select a property by either clicking property overview card or map search box,
    // map zoom level changes to property's bonnds dynamically.
    // we want to set this value minus 3 to add extra zoom out space for user to get back to property overview mode
    let overviewZoomLevel: number

    if (zoomLevel) {
      // minimum zoom level is 3 so we want to set it to 3 at minimum to transition from detail view to overview view
      overviewZoomLevel = zoomLevel <= 5 ? MINIMUM_ZOOM_LEVEL : zoomLevel - 3
    } else {
      overviewZoomLevel = 10
    }

    serviceCenterStore.setState((s) => ({
      ...s,
      propertyOverviewZoomLevel: overviewZoomLevel,
    }))
  },
  resetNodes: () => {
    serviceCenterStore.setState((s) => ({
      ...s,
      plannedNodes: [],
      activeNodes: [],
      nodeLogs: {},
      serviceIssues: {},
    }))
  },
  toggleNodeIconDetailBadge: () => {
    serviceCenterStore.setState((s) => ({
      ...s,
      showIconDetailBadge: !s.showIconDetailBadge,
    }))
  },
}
